import { API_URL } from "../../shared/constants";
import { emptySplitApi } from "../emptySplit-api";

// Epic 7 (Customer Feedback) — Web admin contracts (#4–#14 in
// EPIC_7_MASTER_PROMPT.md §6.5). Kept separate from the legacy
// `feedback-slice.tsx` so the new `feedback.*` permission model and
// `/feedback/*` endpoints do not collide with the old `customerFeedback`
// feature.

export interface FeedbackRatingSummary {
  average: number;
  count: number;
  lowRated: boolean;
}

export interface FeedbackListItem {
  feedbackId: string;
  orderNumber: string;
  storeName: string;
  submittedAt: string;
  ratingSummary: FeedbackRatingSummary;
  commentPreview: string;
}

export interface FeedbackListResponse {
  status: boolean;
  count: number;
  list: FeedbackListItem[];
}

export interface FeedbackListFilter {
  store?: string;
  orderNumber?: string;
  fromDate?: string;
  toDate?: string;
  lowRating?: boolean;
}

export interface FeedbackListArgs {
  search?: string;
  filter?: FeedbackListFilter;
  limit?: number;
  skip?: number;
  sort?: string;
  sortBy?: string;
}

export interface FeedbackRating {
  questionId: string;
  questionText: string;
  rating: number;
}

export interface FeedbackComment {
  commentFieldId: string;
  fieldLabel: string;
  commentText: string;
}

export interface FeedbackSubmittedBy {
  _id: string;
  name: string;
  username: string;
}

export interface FeedbackDetailItem {
  feedbackId: string;
  orderNumber: string;
  storeName: string;
  submittedAt: string;
  submittedBy: FeedbackSubmittedBy;
  ratings: FeedbackRating[];
  comments: FeedbackComment[];
}

export interface FeedbackDetailResponse {
  status: boolean;
  item: FeedbackDetailItem;
}

export interface FeedbackConfigListResponse<T> {
  status: boolean;
  list: T[];
}

export interface FeedbackQuestion {
  questionId: string;
  questionText: string;
  displayOrder: number;
  isActive: boolean;
}

export interface FeedbackCommentField {
  commentFieldId: string;
  fieldLabel: string;
  labelHint?: string;
  displayOrder: number;
  characterLimit: number;
  isActive: boolean;
}

export interface FeedbackQuestionCreateArgs {
  questionText: string;
  displayOrder: number;
  isActive?: boolean;
}

export interface FeedbackQuestionUpdateArgs {
  questionId: string;
  questionText?: string;
  displayOrder?: number;
  isActive?: boolean;
}

export interface FeedbackCommentFieldCreateArgs {
  fieldLabel: string;
  labelHint?: string;
  displayOrder: number;
  characterLimit: number;
  isActive?: boolean;
}

export interface FeedbackCommentFieldUpdateArgs {
  commentFieldId: string;
  fieldLabel?: string;
  labelHint?: string;
  displayOrder?: number;
  characterLimit?: number;
  isActive?: boolean;
}

export interface FeedbackExportArgs {
  format: "csv" | "xlsx";
  search?: string;
  filter?: FeedbackListFilter;
}

export interface FeedbackMutationResponse {
  status: boolean;
  message?: string;
}

export interface FeedbackExportResponse {
  status: boolean;
  item?: string;
  file?: string;
  message?: string;
}

export const customerFeedbackApi = emptySplitApi.injectEndpoints({
  endpoints: (builder) => ({
    // #4 list
    customerFeedbackList: builder.query<FeedbackListResponse, FeedbackListArgs>({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/list`,
        method: "post",
        body: data,
      }),
    }),
    // #5 get
    customerFeedbackGet: builder.query<FeedbackDetailResponse, string>({
      query: (feedbackId) => ({
        url: `/${API_URL.FEEDBACK}/get`,
        method: "post",
        body: { feedbackId },
      }),
    }),
    // #6 export
    customerFeedbackExport: builder.mutation<
      FeedbackExportResponse,
      FeedbackExportArgs
    >({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/export`,
        method: "post",
        body: data,
      }),
    }),
    // #7 list questions
    feedbackQuestionsList: builder.query<
      FeedbackConfigListResponse<FeedbackQuestion>,
      void
    >({
      query: () => ({
        url: `/${API_URL.FEEDBACK}/questions/list`,
        method: "post",
        body: {},
      }),
    }),
    // #8 create question
    feedbackQuestionsCreate: builder.mutation<
      FeedbackMutationResponse,
      FeedbackQuestionCreateArgs
    >({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/questions/create`,
        method: "post",
        body: data,
      }),
    }),
    // #9 update question
    feedbackQuestionsUpdate: builder.mutation<
      FeedbackMutationResponse,
      FeedbackQuestionUpdateArgs
    >({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/questions/update`,
        method: "post",
        body: data,
      }),
    }),
    // #10 delete question
    feedbackQuestionsDelete: builder.mutation<
      FeedbackMutationResponse,
      { questionId: string }
    >({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/questions/delete`,
        method: "post",
        body: data,
      }),
    }),
    // #11 list comment fields
    feedbackCommentFieldsList: builder.query<
      FeedbackConfigListResponse<FeedbackCommentField>,
      void
    >({
      query: () => ({
        url: `/${API_URL.FEEDBACK}/comment-fields/list`,
        method: "post",
        body: {},
      }),
    }),
    // #12 create comment field
    feedbackCommentFieldsCreate: builder.mutation<
      FeedbackMutationResponse,
      FeedbackCommentFieldCreateArgs
    >({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/comment-fields/create`,
        method: "post",
        body: data,
      }),
    }),
    // #13 update comment field
    feedbackCommentFieldsUpdate: builder.mutation<
      FeedbackMutationResponse,
      FeedbackCommentFieldUpdateArgs
    >({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/comment-fields/update`,
        method: "post",
        body: data,
      }),
    }),
    // #14 delete comment field
    feedbackCommentFieldsDelete: builder.mutation<
      FeedbackMutationResponse,
      { commentFieldId: string }
    >({
      query: (data) => ({
        url: `/${API_URL.FEEDBACK}/comment-fields/delete`,
        method: "post",
        body: data,
      }),
    }),
  }),
  overrideExisting: false,
});

export const {
  useCustomerFeedbackListQuery,
  useCustomerFeedbackGetQuery,
  useCustomerFeedbackExportMutation,
  useFeedbackQuestionsListQuery,
  useFeedbackQuestionsCreateMutation,
  useFeedbackQuestionsUpdateMutation,
  useFeedbackQuestionsDeleteMutation,
  useFeedbackCommentFieldsListQuery,
  useFeedbackCommentFieldsCreateMutation,
  useFeedbackCommentFieldsUpdateMutation,
  useFeedbackCommentFieldsDeleteMutation,
} = customerFeedbackApi;
